import React from "react"

import { Button, Card, CardContent, CompletionIndicator } from "@coval/ui"
import type { LucideIcon } from "lucide-react"

import { ReorderableChips } from "./ReorderableChips"

interface EntitySelectionChip {
  id: string
  label: React.ReactNode
  leading?: React.ReactNode
  className?: string
  /** Accessible name for the remove control; falls back to the chip's id. */
  removeLabel?: string
  /** An id whose record is missing reads as an error rather than a selection. */
  isMissing?: boolean
  /** Spread onto the chip, for the view-context attributes Sofia reads. */
  attributes?: Record<string, string | undefined>
}

interface EntitySelectionCardProps {
  icon: LucideIcon
  /** Section heading, e.g. "Agent". Also names the completion indicator. */
  title: string
  /**
   * A line under the heading, for something the selection itself cannot say —
   * that its order is meaningful, say. Omit when the chips speak for themselves.
   */
  description?: React.ReactNode
  chips: EntitySelectionChip[]
  onRemove?: (id: string) => void
  /** Opens the picker dialog. */
  onBrowse: () => void
  /** Drops the whole selection. Offered only while something is selected. */
  onClearAll?: () => void
  /**
   * Enables drag-to-reorder on the chips, called with the source and
   * destination indices. Omit for a section whose order carries no meaning.
   */
  onReorder?: (from: number, to: number) => void
  /**
   * Replaces the default "N selected" summary beside the title — for a section
   * whose count only means something against a limit, e.g. "3 / 25 selected".
   */
  countLabel?: string
  /** Header button, e.g. "Browse Agents". */
  browseLabel: string
  /** Empty-state button, e.g. "Add Agents". */
  addLabel: string
  /**
   * Optional line above the empty state's button. The absence of a selection
   * speaks for itself, so this is for something the user could not otherwise
   * know — a catalog that failed to load, say.
   */
  emptyMessage?: string
  /** Carried by whichever button opens the picker, in either card state. */
  browseTestId?: string
  disabled?: boolean
  /** Rendered below the chips — the section's own configuration, warnings, etc. */
  children?: React.ReactNode
  /** Spread onto the Card, for the view-context attributes Sofia reads. */
  cardProps?: React.HTMLAttributes<HTMLDivElement>
}

/**
 * A configuration section that owns a set of selected entities: the picked ones
 * as chips, a way back into the catalog, and its completion state.
 *
 * Agents, personas, test sets, and metrics all read the same way here on
 * purpose — one selection model across the launcher rather than four.
 */
export const EntitySelectionCard: React.FC<EntitySelectionCardProps> = ({
  icon: SectionIcon,
  title,
  description,
  chips,
  onRemove,
  onBrowse,
  onClearAll,
  onReorder,
  countLabel,
  browseLabel,
  addLabel,
  emptyMessage,
  browseTestId,
  disabled = false,
  children,
  cardProps,
}) => {
  const isEmpty = chips.length === 0

  return (
    <Card className="w-full" {...cardProps}>
      <div
        className={`flex justify-between gap-4 p-6 ${
          description ? "items-start" : "items-center"
        }`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-coval-text">
              <SectionIcon className="size-4 shrink-0" />
              {title}
            </h3>
            {chips.length > 0 && (
              <span className="text-sm text-coval-text-muted">
                {countLabel ?? `${chips.length} selected`}
              </span>
            )}
          </div>
          {description && (
            // Indented past the heading's icon and gap so it starts under the
            // title text rather than under the icon.
            <p className="mt-1 pl-6 text-sm text-coval-text-muted">
              {description}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {!isEmpty && onClearAll && (
            <Button
              type="button"
              variant="ghost"
              onClick={onClearAll}
              disabled={disabled}
              aria-label={`Clear all ${title.toLowerCase()} selections`}
            >
              Clear all
            </Button>
          )}
          {!isEmpty && (
            <Button
              type="button"
              variant="secondary"
              onClick={onBrowse}
              disabled={disabled}
              aria-label={browseLabel}
              data-testid={browseTestId}
            >
              {browseLabel}
            </Button>
          )}
          <CompletionIndicator complete={!isEmpty} label={title} />
        </div>
      </div>
      <CardContent className="space-y-4 px-6 pb-6 pt-0">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center py-2">
            <div className="space-y-2 text-center">
              {emptyMessage ? (
                <p className="text-sm text-coval-danger-text">{emptyMessage}</p>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                onClick={onBrowse}
                disabled={disabled}
                aria-label={addLabel}
                data-testid={browseTestId}
              >
                {addLabel}
              </Button>
            </div>
          </div>
        ) : (
          <ReorderableChips
            chips={chips.map(
              ({
                id,
                label,
                leading,
                className,
                removeLabel,
                isMissing,
                attributes,
              }) => ({
                id,
                label,
                leading,
                className,
                removeLabel,
                invalid: isMissing,
                attributes,
                onRemove:
                  disabled || !onRemove ? undefined : () => onRemove(id),
              }),
            )}
            onReorder={onReorder}
            disabled={disabled}
          />
        )}
        {children}
      </CardContent>
    </Card>
  )
}
